import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layers, Clock, Pill, FlaskConical, Utensils, Activity, Loader2, AlertTriangle,
  CheckCircle, Sun, Sunset, Moon, Star, Coffee, ShieldAlert, Eye, Calendar,
  Plus, Pencil, Trash2, X, RotateCcw, Save
} from 'lucide-react';

const BLOCK_META = {
  wake: { label: 'Wake', icon: Sun, color: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  morning: { label: 'Morning', icon: Sun, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  mid_morning: { label: 'Mid-Morning', icon: Coffee, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  breakfast: { label: 'Breakfast', icon: Utensils, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  lunch: { label: 'Lunch', icon: Utensils, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  afternoon: { label: 'Afternoon', icon: Sunset, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  evening: { label: 'Evening', icon: Sunset, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  dinner: { label: 'Dinner', icon: Utensils, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
  night: { label: 'Night', icon: Moon, color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' },
  sleep: { label: 'Sleep', icon: Star, color: 'text-indigo-300', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' },
  anytime: { label: 'Anytime', icon: Clock, color: 'text-white/40', bg: 'bg-adm-surface/5', border: 'border-white/10' },
};

const TYPE_ICONS = {
  regenflo: { icon: FlaskConical, color: 'text-[#4E7A6B]', bg: 'bg-[#4E7A6B]/15' },
  medication: { icon: Pill, color: 'text-blue-400', bg: 'bg-blue-500/15' },
  food: { icon: Utensils, color: 'text-amber-400', bg: 'bg-amber-500/15' },
  life_event: { icon: Activity, color: 'text-purple-400', bg: 'bg-purple-500/15' },
  routine: { icon: Clock, color: 'text-white/50', bg: 'bg-adm-surface/10' },
};

const BLOCK_ORDER = ['wake', 'morning', 'mid_morning', 'breakfast', 'lunch', 'afternoon', 'evening', 'dinner', 'night', 'sleep', 'anytime'];

function format24to12(t24) {
  if (!t24) return '';
  const [h, m] = t24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// ── Inline Add/Edit Item Form ────────────────────────────
function ItemForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || {
    code: '', title: '', item_type: 'regenflo', timing_text: '',
    dose_text: '', instruction_text: '', category_axis: '', visibility: 'both',
  });
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const BLOCKS = ['wake', 'morning', 'mid_morning', 'breakfast', 'lunch', 'afternoon', 'evening', 'dinner', 'night', 'sleep', 'anytime'];

  return (
    <div className="p-4 rounded-xl bg-adm-bg/80 border border-[#4E7A6B]/30 space-y-3" data-testid="item-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-[10px] text-white/50">Code</Label>
          <Input value={form.code} onChange={e => u('code', e.target.value)} className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]" placeholder="e.g. P1-C01" data-testid="item-code" /></div>
        <div><Label className="text-[10px] text-white/50">Title</Label>
          <Input value={form.title} onChange={e => u('title', e.target.value)} className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]" placeholder="Item name" data-testid="item-title" /></div>
        <div><Label className="text-[10px] text-white/50">Type</Label>
          <Select value={form.item_type} onValueChange={v => u('item_type', v)}>
            <SelectTrigger className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]" data-testid="item-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-adm-surface border-white/[0.06]">
              <SelectItem value="regenflo">ReGenflō</SelectItem>
              <SelectItem value="medication">Medication</SelectItem>
              <SelectItem value="food">Food</SelectItem>
              <SelectItem value="life_event">Life Event</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label className="text-[10px] text-white/50">Time</Label>
          <Input value={form.timing_text} onChange={e => u('timing_text', e.target.value)} className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]" placeholder="e.g. 07:00" data-testid="item-timing" /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-[10px] text-white/50">Dose</Label>
          <Input value={form.dose_text} onChange={e => u('dose_text', e.target.value)} className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]" placeholder="e.g. 1 tablet" /></div>
        <div><Label className="text-[10px] text-white/50">Block</Label>
          <Select value={form.category_axis || ''} onValueChange={v => u('category_axis', v)}>
            <SelectTrigger className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]" data-testid="item-block"><SelectValue placeholder="Select block..." /></SelectTrigger>
            <SelectContent className="bg-adm-surface border-white/[0.06]">
              {BLOCKS.map(b => <SelectItem key={b} value={b}>{b.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div><Label className="text-[10px] text-white/50">Visibility</Label>
          <Select value={form.visibility} onValueChange={v => u('visibility', v)}>
            <SelectTrigger className="mt-1 h-8 text-sm bg-adm-surface border-white/[0.06]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-adm-surface border-white/[0.06]">
              <SelectItem value="both">Both</SelectItem>
              <SelectItem value="subscriber">Subscriber</SelectItem>
              <SelectItem value="care_team">Care Team</SelectItem>
            </SelectContent>
          </Select></div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-[10px] text-white/50 cursor-pointer">
            <input type="checkbox" checked={form.is_critical || false} onChange={e => u('is_critical', e.target.checked)} className="rounded border-white/20" />
            Critical item
          </label>
        </div>
      </div>
      <div><Label className="text-[10px] text-white/50">Instructions / Notes</Label>
        <Textarea value={form.instruction_text} onChange={e => u('instruction_text', e.target.value)} className="mt-1 min-h-[50px] text-sm bg-adm-surface border-white/[0.06]" placeholder="Instructions, watchpoints..." data-testid="item-instructions" /></div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} className="text-white/50 h-8"><X className="w-3 h-3 mr-1" /> Cancel</Button>
        <Button size="sm" onClick={() => onSave(form)} disabled={saving || !form.title.trim()} className="bg-[#4E7A6B] hover:bg-[#3D695B] text-white h-8" data-testid="item-save-btn">
          <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}


export default function ExecutableProtocolReview() {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adherence, setAdherence] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`).then(r => setSubscribers(r.data)).catch(() => {});
  }, []);

  const loadProtocol = useCallback(() => {
    if (!selectedSub) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/executable-protocol/${selectedSub}`),
      axios.get(`${API}/daily-flow/${selectedSub}/adherence?days=7`).catch(() => ({ data: null })),
    ]).then(([protoR, adhR]) => {
      setData(protoR.data);
      setAdherence(adhR.data);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [selectedSub]);

  useEffect(() => { loadProtocol(); }, [loadProtocol]);

  const handleAddItem = async (form) => {
    setSaving(true);
    try {
      await axios.post(`${API}/executable-protocol/${selectedSub}/items`, form);
      toast.success('Item added');
      setAdding(false);
      loadProtocol();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setSaving(false); }
  };

  const handleUpdateItem = async (form) => {
    if (!editingItem) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/executable-protocol/items/${editingItem}`, form);
      toast.success('Item updated');
      setEditingItem(null);
      loadProtocol();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Remove this item from the protocol?')) return;
    try {
      await axios.delete(`${API}/executable-protocol/items/${itemId}`);
      toast.success('Item removed');
      loadProtocol();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Remove this schedule event?')) return;
    try {
      await axios.delete(`${API}/executable-protocol/events/${eventId}`);
      toast.success('Event removed');
      loadProtocol();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset this protocol? All items, events, and rules will be deleted.')) return;
    try {
      await axios.delete(`${API}/executable-protocol/${selectedSub}/reset`);
      toast.success('Protocol reset');
      loadProtocol();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary flex items-center gap-3" data-testid="exec-review-title">
              <Layers className="w-7 h-7 text-[#4E7A6B]" />
              Executable Protocol Review
            </h1>
            <p className="text-adm-primary/60 mt-1">View and manage the active executable protocol</p>
          </div>
          <div className="w-72">
            <Select value={selectedSub} onValueChange={setSelectedSub}>
              <SelectTrigger className="bg-adm-surface border-white/[0.06]" data-testid="exec-subscriber-select">
                <SelectValue placeholder="Select subscriber..." />
              </SelectTrigger>
              <SelectContent className="bg-adm-surface border-white/[0.06]">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#4E7A6B] animate-spin" /></div>
        )}

        {!loading && selectedSub && !data?.has_protocol && (
          <Card className="bg-adm-surface border-white/[0.06]">
            <CardContent className="py-16 text-center">
              <Layers className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 mb-4">No active executable protocol for this subscriber.</p>
              <p className="text-white/30 text-sm">Publish a protocol via the Protocol Wizard, or upload a schedule via Upload Final Schedule.</p>
            </CardContent>
          </Card>
        )}

        {!loading && data?.has_protocol && (
          <div className="space-y-4">
            {/* Protocol Summary + Actions */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                <Card className="bg-adm-surface border-white/[0.06]">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Phase</p>
                    <p className="text-xl font-bold text-[#4E7A6B]">{data.protocol?.phase_name}</p>
                  </CardContent>
                </Card>
                <Card className="bg-adm-surface border-white/[0.06]">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Items</p>
                    <p className="text-xl font-bold text-white/80" data-testid="epr-total-items">{data.total_items}</p>
                  </CardContent>
                </Card>
                <Card className="bg-adm-surface border-white/[0.06]">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Events/Day</p>
                    <p className="text-xl font-bold text-white/80">{data.total_events}</p>
                  </CardContent>
                </Card>
                <Card className="bg-adm-surface border-white/[0.06]">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider">Source</p>
                    <p className="text-sm font-medium text-white/60 capitalize">{data.protocol?.source_type?.replace('_', ' ')}</p>
                  </CardContent>
                </Card>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAdding(true)} className="border-[#4E7A6B]/40 text-[#4E7A6B]" data-testid="epr-add-item-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
                </Button>
                <Button variant="outline" size="sm" onClick={handleReset} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="epr-reset-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                </Button>
              </div>
            </div>

            {/* Add Item Form */}
            {adding && (
              <ItemForm onSave={handleAddItem} onCancel={() => setAdding(false)} saving={saving} />
            )}

            {/* Debug Diagnostics Panel */}
            {data?.debug && (
              <Card className="bg-adm-surface border-amber-500/20" data-testid="epr-debug-panel">
                <CardContent className="p-3">
                  <p className="text-[10px] text-amber-500/70 uppercase tracking-wider mb-2 font-semibold">System Diagnostics</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-white/30">Active Phase ID</p>
                      <p className="text-white/70 font-mono text-[10px]">{data.debug.active_phase_id}</p>
                    </div>
                    <div>
                      <p className="text-white/30">Protocol Items</p>
                      <p className="text-white/70">{data.debug.protocol_items_count}</p>
                    </div>
                    <div>
                      <p className="text-white/30">Subscriber Items</p>
                      <p className={`font-medium ${data.debug.items_synced ? 'text-emerald-400' : 'text-red-400'}`}>
                        {data.debug.subscriber_schedule_items_count} {data.debug.items_synced ? '(synced)' : '(MISMATCH)'}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/30">Events Generated</p>
                      <p className="text-white/70">{data.debug.events_generated_count}</p>
                    </div>
                    <div>
                      <p className="text-white/30">Admin Source</p>
                      <p className="text-white/50 font-mono text-[10px]">{data.debug.source_collection}</p>
                    </div>
                    <div>
                      <p className="text-white/30">Subscriber Source</p>
                      <p className="text-white/50 font-mono text-[10px]">{data.debug.subscriber_source}</p>
                    </div>
                    <div>
                      <p className="text-white/30">Archived Phases</p>
                      <p className="text-white/70">{data.debug.archived_phases_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 7-Day Adherence */}
            {adherence && adherence.logged_days > 0 && (
              <Card className="bg-adm-surface border-white/[0.06]" data-testid="adherence-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#4E7A6B]" /> 7-Day Adherence
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="grid grid-cols-7 gap-2">
                    {Object.entries(adherence.daily_breakdown).map(([date, day]) => {
                      const pct = day.total > 0 ? Math.round((day.as_scheduled / day.total) * 100) : 0;
                      return (
                        <div key={date} className="text-center p-2 rounded-lg bg-adm-surface/[0.03]">
                          <p className="text-[9px] text-white/30">{date.slice(5)}</p>
                          <p className={`text-sm font-bold ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{pct}%</p>
                          <p className="text-[9px] text-white/20">{day.as_scheduled}/{day.total}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Protocol Items List (flat, with edit/delete) */}
            {data.items?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-white/70 flex items-center gap-2 pb-2" data-testid="epr-items-heading">
                  <Layers className="w-4 h-4 text-[#4E7A6B]" /> Protocol Items ({data.items.length})
                </h2>
                <Card className="bg-adm-surface border-white/[0.06] overflow-hidden">
                  <CardContent className="p-0">
                    {data.items.map((item) => {
                      const ti = TYPE_ICONS[item.item_type] || TYPE_ICONS.routine;
                      const TypeIcon = ti.icon;
                      const isEditing = editingItem === item.id;

                      if (isEditing) {
                        return (
                          <div key={item.id} className="p-3 border-b border-white/5">
                            <ItemForm initial={item} onSave={handleUpdateItem} onCancel={() => setEditingItem(null)} saving={saving} />
                          </div>
                        );
                      }

                      return (
                        <div key={item.id} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 group hover:bg-white/[0.02]" data-testid={`epr-item-${item.id}`}>
                          <div className={`w-7 h-7 rounded-lg ${ti.bg} flex items-center justify-center flex-shrink-0`}>
                            <TypeIcon className={`w-3.5 h-3.5 ${ti.color}`} />
                          </div>
                          <span className="text-xs font-mono text-white/40 w-20 flex-shrink-0">{item.code || '—'}</span>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-white/90">{item.title}</span>
                            {item.instruction_text && <p className="text-[10px] text-white/40 truncate">{item.instruction_text}</p>}
                          </div>
                          <span className="text-[10px] text-white/30">{item.timing_text}</span>
                          <span className="text-[10px] text-white/30 capitalize">{item.visibility}</span>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" onClick={() => setEditingItem(item.id)} className="h-7 w-7 p-0 text-white/40 hover:text-white" data-testid={`edit-item-${item.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.id)} className="h-7 w-7 p-0 text-red-400/60 hover:text-red-400" data-testid={`delete-item-${item.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Schedule Events by Block */}
            <h2 className="text-sm font-semibold text-white/70 flex items-center gap-2 pt-2">
              <Eye className="w-4 h-4 text-[#4E7A6B]" /> Daily Schedule Template
            </h2>
            {BLOCK_ORDER.filter(b => data.events_by_block?.[b]).map(block => {
              const meta = BLOCK_META[block];
              const BlockIcon = meta.icon;
              return (
                <Card key={block} className={`bg-adm-surface border ${meta.border}`} data-testid={`review-block-${block}`}>
                  <CardHeader className="pb-1 pt-3 px-4">
                    <div className="flex items-center gap-2">
                      <BlockIcon className={`w-4 h-4 ${meta.color}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
                      <span className="text-[10px] text-white/30">{data.events_by_block[block].length} events</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {data.events_by_block[block].map((evt, i) => {
                      const ti = TYPE_ICONS[evt.item_type] || TYPE_ICONS.routine;
                      const TypeIcon = ti.icon;
                      return (
                        <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-t border-white/5 group">
                          <span className="text-xs font-mono text-white/30 w-16 flex-shrink-0">{format24to12(evt.exact_time)}</span>
                          <div className={`w-7 h-7 rounded-lg ${ti.bg} flex items-center justify-center flex-shrink-0`}>
                            <TypeIcon className={`w-3.5 h-3.5 ${ti.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-white/90">{evt.title || evt.user_visible_title}</span>
                            {evt.subtitle && <p className="text-[10px] text-white/40 truncate">{evt.subtitle || evt.user_visible_subtitle}</p>}
                          </div>
                          {evt.is_grouped && <Badge className="text-[9px] bg-adm-surface/5 text-white/40 border-0">{evt.linked_item_codes?.length} items</Badge>}
                          <Badge className="text-[9px] bg-adm-surface/5 text-white/30 border-0 capitalize">{evt.default_confirmation_mode?.replace('_', ' ')}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(evt.id)} className="h-7 w-7 p-0 text-red-400/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-event-${evt.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}

            {/* Rules & Watchpoints */}
            {(data.rule_groups?.length > 0 || data.watchpoints?.length > 0) && (
              <Card className="dark:bg-amber-500/5 dark:border-amber-500/20" data-testid="review-rules">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-amber-400 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> Rules & Watchpoints
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.rule_groups?.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <Badge className={`text-[9px] flex-shrink-0 ${r.warning_level === 'critical' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                        {r.warning_level}
                      </Badge>
                      <div>
                        <span className="text-white/70 font-medium">{r.title}</span>
                        <p className="text-white/40">{r.description}</p>
                      </div>
                    </div>
                  ))}
                  {data.watchpoints?.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <Badge className="text-[9px] bg-purple-500/10 text-purple-400 border-purple-500/20 flex-shrink-0">watch</Badge>
                      <div>
                        <span className="text-white/70 font-medium">{w.title}</span>
                        <p className="text-white/40">{w.description}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
